import { get, post } from "api/apiBridges/httpBridge";
import { useCallback, useEffect, useMemo, useState, useRef, useContext } from "react";
import Button from '@mui/material/Button';
import { 
    Box, Dialog, IconButton, MenuItem, Select, Typography, 
    FormControl, InputLabel 
} from "@mui/material";
import Scrollbars from "rc-scrollbars";
import { useSelector } from "react-redux";
import { loginSelector } from "store/slices/loginSlice";
import CryptoJS from "crypto-js";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Твои компоненты
import { useLocalNotifications } from "components/Events";
import UserAvatar from "./UserAvatar";
import { getDataSource } from "api/ds";
import { ReportTable } from "components/reportTable";
import usePreventDoubleClick from "components/AntiDBC";
import { SelectMarketLocal } from 'components/SelectMarket';
import Loading from 'components/Load';
import { colors } from "styles/colors";
import { useNavigate } from "react-router-dom";
import { useDataContextDeal } from "actions/contextDealClient";
import { SocketContext } from "components/socketLib/ConnectorSocket";

const ContactItem = ({ value, type, onCopy }) => (
    <Box display="flex" alignItems="center" sx={{ '&:hover .copy-icon': { opacity: 1 } }}>
        <Typography variant='body2' sx={{ fontSize: '12pt' }}>
            {value}
        </Typography>
        <IconButton
            size="medium"
            onClick={() => onCopy(value, type)}
            sx={{ ml: 0.5, p: 0 }}
            className="copy-icon"
        >
            <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} />
        </IconButton>
    </Box>
);

export default function CliInfo({ ds, cli_id}) {
    const [loading, setLoading] = useState(true);
    const [allDeals, setAllDeals] = useState([]); // Это наш массив сделок (tmp)
    const [deals, setDeals] = useState([]);
    const [market, setMarket] = useState('wb');
    const isInitialMount = useRef(true);

    const [data, setData] = useState({
        email: "",
        fio: "",
        mobile_number: "",
        photo: undefined
    });

    // --- СОСТОЯНИЯ ДЛЯ МЕНЕДЖЕРОВ ---
    const [managersList, setManagersList] = useState([]);
    const [currentManager, setCurrentManager] = useState(null);
    const [prevManager, setPrevManager] = useState(null);
    const [changeMngOpen, setChangeMngOpen] = useState(false);
    const [selectedNewManagerId, setSelectedNewManagerId] = useState('');

    const $ds = useMemo(() => getDataSource('createDeliveryEI'), []);
    const { showNotification } = useLocalNotifications();
    const preventDoubleClick = usePreventDoubleClick();

    const [infoOpen, setInfoOpen] = useState(false);
    const [tmpPass, setTmpPass] = useState(null);

    const { setContextData } = useDataContextDeal();
    const navigate = useNavigate();

    const { socket } = useContext(SocketContext);

    const { company_id, rules } = useSelector(
        loginSelector
    );

    const handleMarketChange = useCallback((m) => {
        if (isInitialMount.current) isInitialMount.current = false;
        setMarket(m);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const responseData = await get('Clients/get', { cid: cli_id });
                const mngData = await get('Admins/getOnCompany', {cid: company_id});

                let actualMngList = [];
                if (Array.isArray(mngData)) {
                    actualMngList = mngData;
                }

                setManagersList(actualMngList);

                if (responseData?.message) {
                    let photo = undefined;
                    if (responseData.message.data) {
                        let clidata = JSON.parse(responseData.message.data);
                        photo = clidata?.photo;
                    }
                    
                    setData({
                        email: responseData.message.email || "Не указано",
                        fio: responseData.message.fio || "Не указано",
                        mobile_number: responseData.message.mobile_number || "Не указано",
                        photo
                    });

                    let currMng = actualMngList.find(m => Number(m.id) === Number(responseData.message.manager_id));
                    setCurrentManager(currMng || null);
                }

                let tmp = await get('Deal/getClientDeals', { cid: cli_id, market});
                setAllDeals(tmp || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [cli_id]);

    useEffect(() => {
        if (allDeals.length === 0 && !loading) {
            setDeals([]);
            return;
        }
        if (market) {
            const filtered = allDeals.filter(deal => deal.market === market);
            setDeals(filtered.sort((a, b) => a.data.cdn - b.data.cdn));
        } else {
            setDeals(allDeals);
        }
    }, [market, allDeals, loading]);

    const DialoghandleAdd = async () => {
        let pass = await post("Registration/regenClientPass", { user_id: cli_id });
        setTmpPass(CryptoJS.AES.decrypt(pass.message, 'pizdec').toString(CryptoJS.enc.Utf8));
        setInfoOpen(true);
    };

    const showProducts = async () => {
        setContextData({cli_id})
        navigate('/ClientsProducts');
    }

    // --- ОБНОВЛЕННАЯ ФУНКЦИЯ СМЕНЫ МЕНЕДЖЕРА ---
    const handleSaveNewManager = async () => {
        if (!selectedNewManagerId) return;
        try {
            const newMng = managersList.find(m => Number(m.id) === Number(selectedNewManagerId));
            if (!newMng) return;


            // Меняем менеджера у самого клиента
            await post("Clients/addupdate", { id: cli_id, manager_id: newMng.id });

            // 2. Проходим по всем сделкам клиента (tmp), меняем в них manager_id и создаем логи
            for (let deal of allDeals) {
                await post("Deal/update", { id: deal.id, manager_id: newMng.id });

                // Создаем лог по твоему примеру
                await post("Logs/create", {
                    deal_id: deal.id,
                    user_type: "auto",
                    user_id: 0,
                    type: "change_deal_manager", // Тип лога для смены менеджера
                    data: {
                        oldManager: currentManager ? { id: currentManager.id, fio: currentManager.fio } : "Не был назначен",
                        newManager: { id: newMng.id, fio: newMng.fio },
                    }
                });
            }

            // 3. Обновляем локальный стейт сделок, чтобы manager_id обновился в таблице без перезагрузки
            const updatedDeals = allDeals.map(d => ({ ...d, manager_id: newMng.id }));
            setAllDeals(updatedDeals);

            socket.emit("my_clients_change_manager", {oldMng: currentManager?.id || undefined, newMng: newMng.id, company_id})
            // 4. Запоминаем историю и обновляем текущего менеджера в карточке
            if (currentManager) setPrevManager({ ...currentManager });
            setCurrentManager(newMng);

            showNotification({ id: 'mngOk', type: 'success', message: 'Менеджер и сделки успешно обновлены' });
            setChangeMngOpen(false);
            setSelectedNewManagerId('');
        } catch (err) {
            console.error(err);
            showNotification({ id: 'mngErr', type: 'error', message: 'Ошибка при обновлении данных' });
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => showNotification({ id: 'copy_ok', type: 'success', message: 'Скопировано' }))
            .catch(() => showNotification({ id: 'copy_err', type: 'error', message: 'Ошибка копирования' }));
    };

    const checkRoles = (role) => {
    if (rules.includes("admin")) {return true}
  }

    if (loading) return <Loading />;

    return (
        <>
            <Dialog
                open={changeMngOpen}
                onClose={() => setChangeMngOpen(false)}
                PaperProps={{ sx: { borderRadius: "20px", padding: "20px", minWidth: "350px" } }}
            >
                <Typography variant="h6" sx={{ mb: 2 }}>Выберите менеджера</Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Сотрудник</InputLabel>
                    <Select
                        value={selectedNewManagerId}
                        label="Сотрудник"
                        onChange={(e) => setSelectedNewManagerId(e.target.value)}
                    >
                        {Array.isArray(managersList) && managersList.map((mng) => (
                            <MenuItem key={mng.id} value={mng.id}>
                                {`ID: ${mng.id} | ${mng.fio}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box display="flex" justifyContent="space-between">
                    <Button onClick={() => setChangeMngOpen(false)}>Отмена</Button>
                    <Button 
                        variant="contained" 
                        onClick={preventDoubleClick(handleSaveNewManager, 1000)}
                        disabled={!selectedNewManagerId}
                        sx={{ backgroundColor: colors.elements }}
                    >
                        Сохранить и обновить сделки
                    </Button>
                </Box>
            </Dialog>

            <Dialog open={infoOpen} onClose={() => {setInfoOpen(false); setTmpPass(null)}} PaperProps={{ sx: { borderRadius: "40px" } }}>
                <Box sx={{ p: 4, textAlign: 'center', width: '500px' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Разовая информация для клиента</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#078376', mb: 2 }}>Пароль: {tmpPass}</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>Информация отправлена на почту</Typography>
                    <Button variant="contained" onClick={() => {setInfoOpen(false); setTmpPass(null)}} sx={{ backgroundColor: '#078376' }}>Закрыть</Button>
                </Box>
            </Dialog>

            <Box display="flex" flexDirection="column" marginLeft={"15px"} marginTop={"15px"} sx={{ height: "90%" }}>
                <Box display="flex" alignItems="center" sx={{ height: "10%", justifyContent: 'space-between', width: '98%' }}>
                    <Box display="flex" alignItems="center">
                        <UserAvatar src={data.photo} userData={data} user_id={cli_id} size={50} />
                        <Typography variant="body1" sx={{ ml: 2 }}>{data.fio}</Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" sx={{ minWidth: '200px' }}>
                        <ContactItem value={data.mobile_number} type="Телефон" onCopy={handleCopy} />
                        <ContactItem value={data.email} type="Email" onCopy={handleCopy} />
                    </Box>
                </Box>

                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    {checkRoles('genPass') ?
                        <Button 
                            variant="contained" 
                            onClick={preventDoubleClick(DialoghandleAdd, 1000)} 
                            sx={{ textTransform: 'none', fontWeight: 'bold', backgroundColor: '#078376', width: 'fit-content', mt: 1 }}
                        >
                            Сгенерировать новый пароль
                        </Button>
                        :
                        <div></div>
                    }
                    <Button 
                        variant="contained" 
                        onClick={preventDoubleClick(showProducts, 1000)} 
                        sx={{ textTransform: 'none', fontWeight: 'bold', backgroundColor: '#078376', width: 'fit-content', mt: 1 }}
                    >
                        Товары клиента
                    </Button>
                </div>

                <Box 
                    sx={{ 
                        mt: '15px', mb: 2, mr: 2, p: 2, 
                        borderRadius: '15px', backgroundColor: '#f5f5f5',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <Box>
                        <Typography variant="caption" color="textSecondary">Ответственный менеджер:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {currentManager ? `${currentManager.fio} (ID: ${currentManager.id})` : "Не назначен"}
                        </Typography>
                        {currentManager && (
                            <Typography variant="body2" color="textSecondary">
                                {currentManager.mobile_number} | {currentManager.email}
                            </Typography>
                        )}
                        
                        {prevManager && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666', fontStyle: 'italic' }}>
                                Был ранее: {prevManager.fio} (ID: {prevManager.id})
                            </Typography>
                        )}
                    </Box>
                    {checkRoles("changemanager") &&
                        <Button 
                            variant="outlined" 
                            onClick={() => setChangeMngOpen(true)}
                            sx={{ textTransform: 'none', fontWeight: 'bold', color: '#078376', borderColor: '#078376' }}
                        >
                            Сменить менеджера
                        </Button>
                    }
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" marginTop="15px">
                    <Typography variant="h3" component="h1">Сделки клиента</Typography>
                    <SelectMarketLocal markets={['wb', 'ozon', 'consum', 'custom']} market={market} save={handleMarketChange} user_id={cli_id} />
                </Box>

                <div style={{ height: "65%", marginTop: '10px' }}>
                    <Scrollbars autoHide>
                        <ReportTable ds={$ds} data={deals} autoPagination={15} />
                    </Scrollbars>
                </div>

                
            </Box>
        </>
    );
}